/**
 * useAuth — Dual-mode authentication hook
 *
 * Supports two login methods simultaneously:
 *   1. SSO via MSAL (GS1 STS / Azure AD) — existing flow
 *   2. Username / password — new local auth backed by server.js + JWT
 *
 * The active session is stored in sessionStorage.
 * SSO session is managed by MSAL; local session uses a JWT token.
 */
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { loginRequest, graphConfig, isAzureAD } from './msalConfig';

// ── Constants ─────────────────────────────────────────────────────────────────
const LOCAL_TOKEN_KEY = 'iassess_token';
const LOCAL_USER_KEY  = 'iassess_user';
const AUTH_MODE_KEY   = 'iassess_auth_mode'; // 'sso' | 'local'

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

function isTokenExpired(token) {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

function extractUserFromClaims(account) {
  if (!account) return null;
  const claims = account.idTokenClaims || {};
  const name =
    claims.name ||
    (claims.given_name || claims.family_name
      ? `${claims.given_name || ''} ${claims.family_name || ''}`.trim()
      : null) ||
    account.name || account.username || claims.preferred_username || claims.sub || 'Unknown User';
  const email =
    claims.email || claims.upn || claims.preferred_username || account.username || '';
  return { name, email, claims, homeAccountId: account.homeAccountId, source: 'sso' };
}

async function fetchGraphUser(accessToken) {
  try {
    const res = await fetch(graphConfig.userInfoEndpoint, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const d = await res.json();
    return {
      name: d.displayName || d.givenName || '',
      email: d.mail || d.userPrincipalName || '',
      givenName: d.givenName, surname: d.surname,
      jobTitle: d.jobTitle, department: d.department,
      id: d.id, source: 'graph',
    };
  } catch { return null; }
}

// ── Context ───────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // ── Local auth state ──────────────────────────────────────────────────────
  const [localUser, setLocalUser]   = useState(null);
  const [localReady, setLocalReady] = useState(false);

  // ── SSO (MSAL) state ──────────────────────────────────────────────────────
  const { instance, accounts, inProgress } = useMsal();
  const isSsoAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});
  const [graphUser, setGraphUser] = useState(null);

  // Restore local session on mount
  useEffect(() => {
    const token  = sessionStorage.getItem(LOCAL_TOKEN_KEY);
    const stored = sessionStorage.getItem(LOCAL_USER_KEY);
    const mode   = sessionStorage.getItem(AUTH_MODE_KEY);
    if (mode === 'local' && token && stored && !isTokenExpired(token)) {
      try { setLocalUser(JSON.parse(stored)); } catch { /* ignore */ }
    } else if (mode === 'local') {
      sessionStorage.removeItem(LOCAL_TOKEN_KEY);
      sessionStorage.removeItem(LOCAL_USER_KEY);
      sessionStorage.removeItem(AUTH_MODE_KEY);
    }
    setLocalReady(true);
  }, []);

  // Fetch Graph user when SSO is active (Azure AD mode only)
  useEffect(() => {
    if (!isAzureAD || !isSsoAuthenticated || !account) { setGraphUser(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { accessToken } = await instance.acquireTokenSilent({ ...loginRequest, account });
        if (!cancelled) setGraphUser(await fetchGraphUser(accessToken));
      } catch { if (!cancelled) setGraphUser(null); }
    })();
    return () => { cancelled = true; };
  }, [isSsoAuthenticated, account, instance]);

  // ── SSO login ─────────────────────────────────────────────────────────────
  const loginSSO = useCallback(() => {
    instance.loginRedirect(loginRequest).catch(e =>
      console.error('[useAuth] SSO loginRedirect error:', e)
    );
  }, [instance]);

  // ── Local login ───────────────────────────────────────────────────────────
  const loginLocal = useCallback(async (username, password) => {
    const apiBase = process.env.REACT_APP_DB_API_URL;
    const res  = await fetch(`${apiBase}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');

    sessionStorage.setItem(LOCAL_TOKEN_KEY, data.token);
    sessionStorage.setItem(LOCAL_USER_KEY, JSON.stringify(data.user));
    sessionStorage.setItem(AUTH_MODE_KEY, 'local');
    setLocalUser(data.user);
    return data.user;
  }, []);

  // ── Logout (both modes) ───────────────────────────────────────────────────
  // Finding 4 fix: Call the server-side logout endpoint BEFORE clearing
  // sessionStorage so the JWT is revoked server-side and cannot be reused
  // even if it has not yet expired.
  const logout = useCallback(async () => {
    // Attempt server-side token revocation for local JWT sessions
    const token = sessionStorage.getItem(LOCAL_TOKEN_KEY);
    const mode  = sessionStorage.getItem(AUTH_MODE_KEY);

    if (mode === 'local' && token) {
      try {
        const apiBase = process.env.REACT_APP_DB_API_URL;
        await fetch(`${apiBase}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (e) {
        // Non-blocking — even if the revocation call fails, we still clear
        // the local session so the user is logged out on the client side.
        console.warn('[useAuth] Server-side logout call failed:', e.message);
      }
    }

    // Clear local session
    sessionStorage.removeItem(LOCAL_TOKEN_KEY);
    sessionStorage.removeItem(LOCAL_USER_KEY);
    sessionStorage.removeItem(AUTH_MODE_KEY);
    setLocalUser(null);

    // If SSO is also active, log out of MSAL too
    if (isSsoAuthenticated) {
      instance.logoutRedirect({
        postLogoutRedirectUri:
          process.env.REACT_APP_SSO_POST_LOGOUT_URI ||
          process.env.REACT_APP_SSO_REDIRECT_URI ||
          window.location.origin,
      }).catch(e => console.error('[useAuth] SSO logoutRedirect error:', e));
    }
  }, [isSsoAuthenticated, instance]);

  // ── Get access token ──────────────────────────────────────────────────────
  const getAccessToken = useCallback(async () => {
    // Local auth: return JWT from sessionStorage
    const localToken = sessionStorage.getItem(LOCAL_TOKEN_KEY);
    if (localToken && !isTokenExpired(localToken)) return localToken;

    // SSO: acquire token silently
    if (isSsoAuthenticated && account) {
      try {
        const { accessToken } = await instance.acquireTokenSilent({ ...loginRequest, account });
        return accessToken;
      } catch {
        instance.acquireTokenRedirect({ ...loginRequest, account });
        return null;
      }
    }
    return null;
  }, [isSsoAuthenticated, account, instance]);

  // ── Resolved values ───────────────────────────────────────────────────────
  const isAuthenticated = !!localUser || isSsoAuthenticated;
  const isLoading = !localReady || inProgress !== 'none';

  const ssoUser = isAzureAD
    ? (graphUser || extractUserFromClaims(account))
    : extractUserFromClaims(account);

  // Local user takes priority if both are somehow active
  const user = localUser || ssoUser;

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      /** 'local' | 'sso' | null */
      authMode: localUser ? 'local' : (isSsoAuthenticated ? 'sso' : null),
      loginSSO,
      loginLocal,
      logout,
      getAccessToken,
      // Legacy alias so existing code calling login() still works (triggers SSO)
      login: loginSSO,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
